import { useState, useEffect } from 'react';
import { AlertTriangle, Download, Trash2, Loader2, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

type DeletionStep = 'idle' | 'confirm' | 'reason' | 'submitting' | 'done' | 'pending';

export function AccountDangerZone() {
  const { profile, organization, user } = useAuth();
  const [step, setStep] = useState<DeletionStep>('idle');
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState<{ scheduled_delete_at: string } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const isOwner = profile?.role === 'owner';
  const orgName = organization?.name || 'this account';
  const userEmail = user?.email || '';

  // Check for pending deletion on mount
  useEffect(() => {
    supabase
      .from('account_deletion_requests')
      .select('scheduled_delete_at')
      .eq('status', 'pending')
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setPendingDeletion(data);
          setStep('pending');
        }
      });
  }, []);

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      // Fetch key data tables for export
      const [orders, products, customers, locations] = await Promise.all([
        supabase.from('orders').select('*, order_items(*)').order('created_at', { ascending: false }).limit(10000),
        supabase.from('products').select('*'),
        supabase.from('customers').select('*'),
        supabase.from('locations').select('*'),
      ]);

      const exportData = {
        exported_at: new Date().toISOString(),
        organization: organization?.name,
        orders: orders.data || [],
        products: products.data || [],
        customers: customers.data || [],
        locations: locations.data || [],
      };

      // Download as JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blazepos-export-${organization?.slug || 'data'}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Data exported successfully');
    } catch (err) {
      toast.error('Failed to export data');
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRequestDeletion = async () => {
    setStep('submitting');
    try {
      const { data, error } = await supabase.rpc('request_account_deletion', {
        p_reason: reason || null,
      });

      if (error) throw error;

      const result = data as any;
      setPendingDeletion({ scheduled_delete_at: result.scheduled_delete_at });
      setStep('done');

      // Send deletion email
      try {
        await supabase.functions.invoke('send-email', {
          body: {
            type: 'account_deletion_requested',
            to: userEmail,
            data: {
              firstName: profile?.first_name,
              organizationName: organization?.name,
              scheduledDate: result.scheduled_delete_at,
              cancelLink: `${window.location.origin}/settings`,
            },
          },
        });
      } catch {
        // Non-blocking
      }

      toast.success('Account deletion scheduled');
    } catch (err: any) {
      toast.error(err.message || 'Failed to request deletion');
      setStep('reason');
    }
  };

  const handleCancelDeletion = async () => {
    setIsCancelling(true);
    try {
      const { error } = await supabase.rpc('cancel_account_deletion');
      if (error) throw error;

      setPendingDeletion(null);
      setStep('idle');
      toast.success('Account deletion cancelled');
    } catch (err: any) {
      toast.error(err.message || 'Failed to cancel deletion');
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="border-t pt-6">
      <h4 className="font-medium text-red-600 mb-2">Danger Zone</h4>

      <div className="space-y-4">
        {/* Export Data */}
        <div className="p-4 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-card">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium">Export Your Data</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Download all your orders, products, customers, and locations as JSON
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleExportData} disabled={isExporting}>
              {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export
            </Button>
          </div>
        </div>

        {/* Delete Account */}
        <div className="p-4 rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900">
          {step === 'pending' ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h4 className="font-medium text-red-700 dark:text-red-400">Deletion Scheduled</h4>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mb-1">
                Your account is scheduled for deletion on{' '}
                <strong>
                  {pendingDeletion?.scheduled_delete_at
                    ? new Date(pendingDeletion.scheduled_delete_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    : 'soon'}
                </strong>.
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                All data will be permanently removed after this date. You can cancel before then.
              </p>
              <Button variant="outline" size="sm" onClick={handleCancelDeletion} disabled={isCancelling}>
                {isCancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                Cancel Deletion
              </Button>
            </div>
          ) : step === 'done' ? (
            <div className="text-center py-4">
              <Check className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="font-medium text-red-700 dark:text-red-400">Deletion scheduled</p>
              <p className="text-sm text-muted-foreground mt-1">
                You have 7 days to change your mind. Check your email for details.
              </p>
            </div>
          ) : step === 'confirm' || step === 'reason' || step === 'submitting' ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Trash2 className="w-5 h-5 text-red-500" />
                <h4 className="font-medium text-red-700 dark:text-red-400">Confirm Account Deletion</h4>
              </div>

              {step === 'confirm' && (
                <>
                  <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                    This will schedule deletion of <strong>{orgName}</strong> and all associated data
                    (orders, products, customers, team members). You will have 7 days to cancel.
                  </p>
                  <p className="text-sm text-muted-foreground mb-2">
                    We recommend exporting your data first.
                  </p>
                  <div className="space-y-3 mt-4">
                    <div>
                      <Label className="text-sm">Type <strong>DELETE</strong> to confirm</Label>
                      <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="DELETE"
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setStep('idle'); setConfirmText(''); }}>
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={confirmText !== 'DELETE'}
                        onClick={() => setStep('reason')}
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {(step === 'reason' || step === 'submitting') && (
                <>
                  <p className="text-sm text-muted-foreground mb-3">
                    Optional: Help us improve by sharing why you are leaving.
                  </p>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="What could we have done better? (optional)"
                    rows={3}
                    className="mb-3"
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setStep('confirm')}>
                      Back
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={step === 'submitting'}
                      onClick={handleRequestDeletion}
                    >
                      {step === 'submitting' ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scheduling...</>
                      ) : (
                        'Schedule Deletion'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-medium">Delete Account</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Schedule account deletion with a 7-day cooling off period
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                disabled={!isOwner}
                onClick={() => setStep('confirm')}
              >
                Delete Account
              </Button>
            </div>
          )}
        </div>

        {!isOwner && step === 'idle' && (
          <p className="text-xs text-muted-foreground">
            Only the account owner can delete the account.
          </p>
        )}
      </div>
    </div>
  );
}
